// What a company tells the AI (D-061..D-065), checked the same way the database checks it (ORG-06) so an owner sees
// the problem before saving. The database stays the authority.
import type { Json } from "./database.types";

/** A company's business type (organizations.vertical, ORG-09): it decides the starting categories and checklist. */
export type BusinessType = "real_estate" | "software" | "education";
export const BUSINESS_TYPES: { value: BusinessType; label: string }[] = [
  { value: "real_estate", label: "Real estate" },
  { value: "software", label: "Software and IT services" },
  { value: "education", label: "Education and training" },
];

/** What set_business_type() changed. */
export interface BusinessTypeResult {
  vertical: BusinessType;
  previous: BusinessType;
  categories_added: number;
  categories_hidden: number;
  checklist_replaced: boolean;
}

export function asBusinessType(value: unknown): BusinessType {
  return value === "software" || value === "education" ? value : "real_estate";
}

/** Example wording for each box on the AI instructions page, in the company's business type. */
export const EXAMPLES: Record<
  BusinessType,
  Record<TextField | "checklist" | "fact" | "factHint" | "category" | "categoryDescription", string>
> = {
  real_estate: {
    about: "We sell 2 and 3 BHK flats in Green Meadows, Whitefield (from 85 lakh, possession December 2027). " +
      "Site visits daily 10 to 6. This month: no floor-rise charge.",
    vocabulary: "Green Meadows\nWhitefield\nSarjapur Road",
    competitors: "Prestige\nBrigade",
    interest_guide: "Hot: wants a site visit this week and the budget fits. Warm: interested, no visit yet.",
    cold_guide: "Most cold leads come from 99acres and MagicBricks. The goal is a site visit.",
    conversion_guide: "Mention the no floor-rise offer. A token of 1 lakh holds a flat for 15 days.",
    extra: "Note if the customer is an NRI.",
    checklist: "Asked about the family's decision maker",
    fact: "Loan needed",
    factHint: "Yes or no",
    category: "Vastu",
    categoryDescription: "Facing, vastu doubts",
  },
  software: {
    about: "We sell a CRM for small businesses: plans from 999 a user a month, a free 14-day trial, setup in a day. " +
      "Our customers are clinics, schools and agencies.",
    vocabulary: "Our product names\nPlan names\nTools customers mention",
    competitors: "Zoho\nFreshworks\nSpreadsheets",
    interest_guide: "Hot: wants a demo or a trial this week and has a budget. Warm: interested, no demo yet.",
    cold_guide: "Most cold leads come from our website and ads. The goal is a booked demo.",
    conversion_guide: "Offer the annual plan discount. Ask for the go-ahead or a start date.",
    extra: "Note how many users or branches the customer has.",
    checklist: "Asked how they do it today",
    fact: "Number of users",
    factHint: "How many people would use it",
    category: "Data migration",
    categoryDescription: "Moving their existing data",
  },
  education: {
    about: "We teach AutoCAD, Revit and SolidWorks: courses of 2 to 6 months, weekday and weekend batches, classroom " +
      "in Lucknow and online, with placement help.",
    vocabulary: "Course names\nSoftware names\nBranch names",
    competitors: "Other institutes\nOnline course sites",
    interest_guide: "Hot: wants to enrol or visit the institute this week. Warm: asked about fees or batches.",
    cold_guide: "Most leads come from our website and walk-ins. The goal is a demo class or a visit.",
    conversion_guide: "Give the next batch's start date and the instalments. Ask for the registration fee.",
    extra: "If the student lives far away, offer the online course.",
    checklist: "Asked which course or software they want",
    fact: "Course",
    factHint: "The course or software they want",
    category: "Exam clash",
    categoryDescription: "Exams or college timings clash with the batch",
  },
};

/** A telecaller's call type (TEAM-10); null means they make both kinds. */
export type CallType = "cold" | "conversion";
export type AppliesTo = "all" | CallType;

export interface ChecklistItem {
  key: string;
  label: string;
  applies_to: AppliesTo;
}

export interface FactField {
  key: string;
  label: string;
  hint?: string;
}

export interface AiInstructions {
  about: string;
  vocabulary: string;
  competitors: string;
  interest_guide: string;
  cold_guide: string;
  conversion_guide: string;
  extra: string;
  checklist: ChecklistItem[];
  facts: FactField[];
  version: number;
  updated_at: string;
  updated_by: string | null;
}

/** The longest each text may be, as the database allows. */
export const TEXT_LIMITS = {
  about: 3000,
  vocabulary: 1500,
  competitors: 1000,
  interest_guide: 1500,
  cold_guide: 1500,
  conversion_guide: 1500,
  extra: 1500,
} as const;
export type TextField = keyof typeof TEXT_LIMITS;

export const MAX_CHECKLIST = 12;
export const MAX_FACTS = 8;
export const MAX_NOTE = 500;
export const MAX_CATEGORIES = 20;

/** The facts every analysis notes (PIPE-14); a company adds up to eight of its own. */
export const BUILT_IN_FACTS: FactField[] = [
  { key: "budget", label: "Budget" },
  { key: "requirement", label: "Requirement" },
  { key: "location", label: "Location" },
  { key: "timeline", label: "Timeline" },
];

export const CALL_TYPES: CallType[] = ["cold", "conversion"];
export const callTypeLabel: Record<CallType, string> = { cold: "Cold calls", conversion: "Conversion calls" };
export const appliesToLabel: Record<AppliesTo, string> = {
  all: "Every call",
  cold: "Cold calls",
  conversion: "Conversion calls",
};

/** The call type as stored, or null for anything else. */
export function asCallType(value: unknown): CallType | null {
  return value === "cold" || value === "conversion" ? value : null;
}

/**
 * A key for a new checklist item or fact, made from its name: lowercase letters, digits and underscores, starting
 * with a letter, and not one already taken. A name in another script gets a numbered key.
 */
export function keyFor(label: string, taken: string[], max = 40): string {
  let base = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, max);
  if (/^[0-9]/.test(base)) base = `item_${base}`.slice(0, max);
  if (!/^[a-z][a-z0-9_]+$/.test(base)) base = "item";
  let key = base;
  for (let n = 2; taken.includes(key); n++) {
    const suffix = `_${n}`;
    key = base.slice(0, max - suffix.length).replace(/_+$/, "") + suffix;
  }
  return key;
}

function text(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** The ai_instructions row as the page uses it; malformed list items are dropped. */
export function parseInstructions(row: {
  about: string;
  vocabulary: string;
  competitors: string;
  interest_guide: string;
  cold_guide: string;
  conversion_guide: string;
  extra: string;
  checklist: Json;
  facts: Json;
  version: number;
  updated_at: string;
  updated_by: string | null;
}): AiInstructions {
  const checklist: ChecklistItem[] = [];
  for (const raw of Array.isArray(row.checklist) ? row.checklist : []) {
    const i = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
    const applies = i.applies_to === "all" || i.applies_to === "cold" || i.applies_to === "conversion"
      ? i.applies_to
      : null;
    if (typeof i.key === "string" && typeof i.label === "string" && applies) {
      checklist.push({ key: i.key, label: i.label, applies_to: applies });
    }
  }
  const facts: FactField[] = [];
  for (const raw of Array.isArray(row.facts) ? row.facts : []) {
    const f = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
    if (typeof f.key === "string" && typeof f.label === "string") {
      facts.push({ key: f.key, label: f.label, ...(typeof f.hint === "string" && f.hint ? { hint: f.hint } : {}) });
    }
  }
  return {
    about: text(row.about),
    vocabulary: text(row.vocabulary),
    competitors: text(row.competitors),
    interest_guide: text(row.interest_guide),
    cold_guide: text(row.cold_guide),
    conversion_guide: text(row.conversion_guide),
    extra: text(row.extra),
    checklist,
    facts,
    version: row.version,
    updated_at: row.updated_at,
    updated_by: row.updated_by,
  };
}

const KEY = /^[a-z][a-z0-9_]{1,39}$/;
const FACT_KEY = /^[a-z][a-z0-9_]{1,29}$/;

export function textProblem(field: TextField, value: string): string | null {
  return value.length > TEXT_LIMITS[field]
    ? `Keep this under ${TEXT_LIMITS[field].toLocaleString("en-IN")} characters.`
    : null;
}

export function checklistProblem(items: ChecklistItem[]): string | null {
  if (items.length > MAX_CHECKLIST) return `The checklist has at most ${MAX_CHECKLIST} items.`;
  const keys = new Set<string>();
  for (const i of items) {
    if (!KEY.test(i.key) || keys.has(i.key)) return "Two checklist items are the same; remove one.";
    keys.add(i.key);
    const label = i.label.trim();
    if (label.length < 3 || label.length > 120) return "Describe each checklist item in 3 to 120 characters.";
  }
  return null;
}

export function factsProblem(facts: FactField[]): string | null {
  if (facts.length > MAX_FACTS) return `At most ${MAX_FACTS} extra facts.`;
  const keys = new Set(BUILT_IN_FACTS.map((f) => f.key));
  for (const f of facts) {
    if (!FACT_KEY.test(f.key) || keys.has(f.key)) return `"${f.label}" is already noted.`;
    keys.add(f.key);
    const label = f.label.trim();
    if (label.length < 2 || label.length > 60) return "Name each fact in 2 to 60 characters.";
    if ((f.hint ?? "").length > 200) return "Keep each fact's hint under 200 characters.";
  }
  return null;
}

/** The lists as saved: trimmed, and without an empty hint. */
export function checklistJson(items: ChecklistItem[]): Json {
  return items.map((i) => ({ key: i.key, label: i.label.trim(), applies_to: i.applies_to }));
}

export function factsJson(facts: FactField[]): Json {
  return facts.map((f) => {
    const hint = (f.hint ?? "").trim();
    return hint ? { key: f.key, label: f.label.trim(), hint } : { key: f.key, label: f.label.trim() };
  });
}

/** How a fact is shown on a lead: the company's own name for it, else its key in words. */
export function factLabel(key: string, facts: FactField[]): string {
  return [...BUILT_IN_FACTS, ...facts].find((f) => f.key === key)?.label ?? key.replace(/_/g, " ");
}

/** One checklist item's result per person over a few days (checklist_summary()). */
export interface ChecklistTally {
  member_id: string;
  item: string;
  done: number;
  missed: number;
}

/** The share of calls an item was done on, or null when it never applied. */
export function doneShare(t: { done: number; missed: number } | undefined): number | null {
  if (!t || t.done + t.missed === 0) return null;
  return t.done / (t.done + t.missed);
}

/** Everyone's tallies for one item added up. */
export function teamTally(rows: ChecklistTally[], item: string): { done: number; missed: number } {
  return rows.filter((r) => r.item === item).reduce(
    (sum, r) => ({ done: sum.done + r.done, missed: sum.missed + r.missed }),
    { done: 0, missed: 0 },
  );
}
